// Pages/almacenamiento/components/EliminarArchivosModal.tsx
//
// Eliminación SEGURA de archivos en dos pasos + resumen (misma lógica que
// Pages/inventario/components/DeleteProductModal.tsx):
//   Paso 1 — POST /almacenamiento/impacto: qué se llevaría realmente (archivos,
//            peso, rango de fechas, módulos, huérfanos, usos, advertencias).
//            Si el cálculo falla se BLOQUEA y se ofrece Reintentar: jamás se
//            muestra "0 impacto" cuando en realidad no se pudo calcular.
//   Paso 2 — ÚLTIMA CONFIRMACIÓN: escribir ELIMINAR habilita el botón rojo.
//   Paso 3 — resumen del resultado (eliminados, liberado, fallidos, ya no existían).
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Input, Modal, Space, Spin, Tag, Typography } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { formatPropertyNames } from "../../../utils/propertyDisplay";
import {
  almacenamientoApi,
  PALABRA_CONFIRMACION,
  type Archivo,
  type ImpactoEliminacion,
  type ResultadoEliminacion,
} from "../../../services/almacenamiento.api";
import {
  confirmacionValida,
  formatDiaChile,
  formatPeso,
  urlDeDescarga,
} from "../almacenamientoUtils";

const { Text } = Typography;

/** Cuántos archivos se listan por nombre en el paso 1. */
const ARCHIVOS_VISIBLES = 8;
/** Hasta cuántos archivos se ofrece "descargar antes" uno por uno. */
const MAX_DESCARGAS_PREVIAS = 5;

export interface EliminarArchivosModalProps {
  open: boolean;
  archivos: Archivo[];
  /** `huboCambios`: conviene refrescar el resumen y la lista. */
  onClose: (huboCambios: boolean) => void;
}

type Paso = 1 | 2 | 3;

const EliminarArchivosModal: React.FC<EliminarArchivosModalProps> = ({
  open,
  archivos,
  onClose,
}) => {
  const [paso, setPaso] = useState<Paso>(1);
  const [calculando, setCalculando] = useState(false);
  const [impacto, setImpacto] = useState<ImpactoEliminacion | null>(null);
  const [falloImpacto, setFalloImpacto] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const [texto, setTexto] = useState("");
  const [eliminando, setEliminando] = useState(false);
  const [falloEliminar, setFalloEliminar] = useState(false);
  const [resultado, setResultado] = useState<ResultadoEliminacion | null>(null);
  // Si un intento de eliminar falló a mitad de camino, algo pudo haberse borrado:
  // al cerrar igual se refresca.
  const [intentado, setIntentado] = useState(false);

  const ids = useMemo(() => archivos.map((a) => a.id), [archivos]);
  const idsKey = ids.join("|");
  const bytesSeleccion = useMemo(
    () => archivos.reduce((s, a) => s + (Number(a.pesoBytes) || 0), 0),
    [archivos],
  );

  // Al abrir (o cambiar los archivos) se reinicia todo.
  useEffect(() => {
    if (!open) return;
    setPaso(1);
    setTexto("");
    setResultado(null);
    setFalloEliminar(false);
    setIntentado(false);
  }, [open, idsKey]);

  // Paso 1: el impacto REAL, calculado por el servidor.
  useEffect(() => {
    if (!open || ids.length === 0) return;
    let vivo = true;
    setCalculando(true);
    setImpacto(null);
    setFalloImpacto(false);
    almacenamientoApi
      .impacto(ids)
      .then((res) => {
        if (!vivo) return;
        if (res?.data) setImpacto(res.data);
        else setFalloImpacto(true);
      })
      .catch(() => {
        if (vivo) setFalloImpacto(true);
      })
      .finally(() => {
        if (vivo) setCalculando(false);
      });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idsKey, recarga]);

  // Lo que REALMENTE se eliminará: los seleccionados menos los que el servidor omite
  // (regla de los 5 minutos, video optimizándose…). Antes la lista los mostraba igual
  // y contradecía el «0 archivos» del resumen.
  const omitidos = useMemo(() => impacto?.omitidos ?? [], [impacto]);
  const aEliminar = useMemo(() => {
    const fuera = new Set(omitidos.map((o) => o.id));
    return archivos.filter((a) => !fuera.has(a.id));
  }, [archivos, omitidos]);

  const confirmado = confirmacionValida(texto, PALABRA_CONFIRMACION);
  const nada = impacto != null && impacto.archivos === 0;
  const titulo = ids.length === 1 ? "1 archivo" : `${ids.length} archivos`;

  const cerrar = () => {
    if (eliminando) return;
    onClose(Boolean(resultado) || intentado);
  };

  const eliminar = async () => {
    if (!confirmado || eliminando) return;
    setEliminando(true);
    setFalloEliminar(false);
    setIntentado(true);
    try {
      const res = await almacenamientoApi.eliminar(ids);
      if (res?.data) {
        setResultado(res.data);
        setPaso(3);
      } else {
        setFalloEliminar(true);
      }
    } finally {
      setEliminando(false);
    }
  };

  // ── Contenido por paso ───────────────────────────────────────────────────
  const paso1 = calculando ? (
    <div className="alm-modal-cargando" role="status">
      <Spin />
      <div style={{ marginTop: 8 }}>Calculando qué se eliminaría…</div>
    </div>
  ) : falloImpacto || !impacto ? (
    <Alert
      type="error"
      showIcon
      message="No se pudo calcular el impacto"
      description="No se pudo consultar qué se eliminaría (falla del servidor o de la conexión). Por seguridad no se puede continuar sin ese cálculo: reintenta."
    />
  ) : (
    <div>
      <Alert
        type="error"
        showIcon
        message="Esta acción es IRREVERSIBLE"
        description="Los archivos se borran del disco y se quitan de los lugares donde se usan. Solo se pueden recuperar restaurando un backup."
        style={{ marginBottom: 12 }}
      />

      {impacto.noEncontrados.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${impacto.noEncontrados.length} de los seleccionados ya no existe${impacto.noEncontrados.length === 1 ? "" : "n"} y se ignorará${impacto.noEncontrados.length === 1 ? "" : "n"}.`}
        />
      )}

      <ul className="alm-impacto-lista">
        <li>
          <Text strong>
            {impacto.archivos.toLocaleString("es-CL")}{" "}
            {impacto.archivos === 1 ? "archivo" : "archivos"}
          </Text>{" "}
          · se liberan <Text strong>{formatPeso(impacto.bytes)}</Text>
        </li>
        <li>
          <Text strong>{impacto.huerfanos.toLocaleString("es-CL")}</Text> huérfano
          {impacto.huerfanos === 1 ? "" : "s"} (nadie los usa) y{" "}
          <Text strong>
            {Math.max(0, impacto.archivos - impacto.huerfanos).toLocaleString("es-CL")}
          </Text>{" "}
          en uso.
        </li>
        <li>
          Desaparecen de <Text strong>{impacto.usos.toLocaleString("es-CL")}</Text>{" "}
          {impacto.usos === 1 ? "lugar" : "lugares"} donde hoy se muestran.
        </li>
        {impacto.rangoFechas && (
          <li>
            Subidos entre el <Text strong>{formatDiaChile(impacto.rangoFechas.desde)}</Text> y el{" "}
            <Text strong>{formatDiaChile(impacto.rangoFechas.hasta)}</Text> (las fechas pueden ser
            estimadas).
          </li>
        )}
      </ul>

      {impacto.porModulo.length > 0 && (
        <div className="alm-impacto-modulos">
          <Text strong>Por módulo</Text>
          <ul>
            {impacto.porModulo.map((m) => (
              <li key={m.modulo}>
                {m.etiqueta}: {m.archivos.toLocaleString("es-CL")}{" "}
                {m.archivos === 1 ? "archivo" : "archivos"} · {formatPeso(m.bytes)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {impacto.advertencias.length > 0 && (
        <div className="alm-impacto-advertencias">
          {impacto.advertencias.map((a) => (
            <Alert
              key={a.codigo}
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
              message={
                <span>
                  {a.mensaje} <Tag color="orange">{a.cantidad}</Tag>
                </span>
              }
            />
          ))}
        </div>
      )}

      {aEliminar.length > 0 && (
        <div className="alm-impacto-archivos">
          <Text strong>Archivos que se eliminarán</Text>
          <ul className="alm-impacto-archivos-lista">
            {aEliminar.slice(0, ARCHIVOS_VISIBLES).map((a) => {
              const uso = a.usos[0];
              const props = uso ? formatPropertyNames(uso.propiedades) : [];
              return (
                <li key={a.id}>
                  <div className="alm-impacto-item">
                    <Text ellipsis={{ tooltip: a.nombre }} className="alm-impacto-nombre">
                      {a.nombre}
                    </Text>
                    <Text type="secondary" className="alm-impacto-dueno">
                      {formatPeso(a.pesoBytes)} ·{" "}
                      {uso
                        ? `${uso.etiquetaModulo}: ${uso.titulo || `#${uso.filaId}`}${props.length ? ` (${props.join(", ")})` : ""}${a.usos.length > 1 ? ` y ${a.usos.length - 1} más` : ""}`
                        : "huérfano, sin dueño"}
                    </Text>
                  </div>
                </li>
              );
            })}
          </ul>
          {aEliminar.length > ARCHIVOS_VISIBLES && (
            <Text type="secondary">y {aEliminar.length - ARCHIVOS_VISIBLES} más…</Text>
          )}
        </div>
      )}

      {omitidos.length > 0 && (
        <div className="alm-impacto-archivos alm-impacto-omitidos">
          <Text strong>No se eliminarán ({omitidos.length})</Text>
          <ul className="alm-impacto-archivos-lista">
            {omitidos.slice(0, ARCHIVOS_VISIBLES).map((o) => (
              <li key={o.id}>
                <div className="alm-impacto-item">
                  <Text ellipsis={{ tooltip: o.nombre }} className="alm-impacto-nombre">
                    {o.nombre}
                  </Text>
                  <Text type="secondary" className="alm-impacto-dueno">
                    {o.motivo}
                  </Text>
                </div>
              </li>
            ))}
          </ul>
          {omitidos.length > ARCHIVOS_VISIBLES && (
            <Text type="secondary">y {omitidos.length - ARCHIVOS_VISIBLES} más…</Text>
          )}
        </div>
      )}

      {aEliminar.length > 0 && (
        <Alert
          style={{ marginTop: 12 }}
          type="info"
          showIcon
          message="¿Quieres conservar una copia?"
          description={
            <div>
              <div>
                Descárgala antes de continuar
                {aEliminar.length > MAX_DESCARGAS_PREVIAS
                  ? " (con tantos archivos conviene un backup completo: menú Backup BD, solo superadministrador)."
                  : ":"}
              </div>
              {aEliminar.length <= MAX_DESCARGAS_PREVIAS && (
                <ul className="alm-descargas">
                  {aEliminar.map((a) => (
                    <li key={a.id}>
                      {a.url ? (
                        <a
                          href={urlDeDescarga(a.url)}
                          download={a.nombre}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Descargar {a.nombre}
                        </a>
                      ) : (
                        <Text type="secondary">{a.nombre} (sin enlace de descarga)</Text>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          }
        />
      )}
    </div>
  );

  const paso2 = (
    <div>
      <Alert
        type="error"
        showIcon
        message={`Se eliminarán ${impacto?.archivos ?? ids.length} ${(impacto?.archivos ?? ids.length) === 1 ? "archivo" : "archivos"} (${formatPeso(impacto?.bytes ?? bytesSeleccion)}) de forma PERMANENTE.`}
        description="No hay papelera ni deshacer. Solo restaurando un backup se recuperan."
        style={{ marginBottom: 12 }}
      />
      {falloEliminar && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="La eliminación no terminó correctamente"
          description="Puede que algunos archivos sí se hayan eliminado. Puedes reintentar (los que ya no existen se ignoran) o cerrar para revisar el estado real."
        />
      )}
      <p id="alm-confirmar-texto">
        Para confirmar, escribe{" "}
        <Text code strong>
          {PALABRA_CONFIRMACION}
        </Text>{" "}
        y presiona el botón rojo:
      </p>
      <Input
        autoFocus
        aria-labelledby="alm-confirmar-texto"
        value={texto}
        disabled={eliminando}
        onChange={(e) => setTexto(e.target.value)}
        placeholder={PALABRA_CONFIRMACION}
        onPressEnter={() => void eliminar()}
      />
    </div>
  );

  const paso3 = resultado && (
    <div>
      <Alert
        type={resultado.fallidos.length > 0 ? "warning" : "success"}
        showIcon
        message={
          resultado.fallidos.length > 0
            ? "La eliminación terminó con observaciones"
            : "Eliminación completada"
        }
        style={{ marginBottom: 12 }}
      />
      <ul className="alm-impacto-lista">
        <li>
          <Text strong>{resultado.eliminados.toLocaleString("es-CL")}</Text>{" "}
          {resultado.eliminados === 1 ? "archivo eliminado" : "archivos eliminados"}
        </li>
        <li>
          Espacio liberado: <Text strong>{formatPeso(resultado.bytesLiberados)}</Text>
        </li>
        {resultado.yaNoExistian > 0 && (
          <li>
            {resultado.yaNoExistian.toLocaleString("es-CL")} ya no existía
            {resultado.yaNoExistian === 1 ? "" : "n"} (alguien más los eliminó antes).
          </li>
        )}
        <li>
          Quedó anotado en la pestaña <Text strong>Registro de eliminados</Text>.
        </li>
      </ul>
      {resultado.fallidos.length > 0 && (
        <div>
          <Text strong type="danger">
            No se pudieron eliminar ({resultado.fallidos.length}):
          </Text>
          <ul className="alm-impacto-archivos-lista">
            {resultado.fallidos.map((f) => (
              <li key={f.id}>
                <div className="alm-impacto-item">
                  <Text ellipsis={{ tooltip: f.nombre }}>{f.nombre}</Text>
                  <Text type="danger">{f.motivo}</Text>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // ── Pie ──────────────────────────────────────────────────────────────────
  const pie =
    paso === 1
      ? [
          <Button key="cancelar" onClick={cerrar}>
            Cancelar
          </Button>,
          falloImpacto ? (
            <Button
              key="reintentar"
              type="primary"
              loading={calculando}
              onClick={() => setRecarga((n) => n + 1)}
            >
              Reintentar
            </Button>
          ) : (
            <Button
              key="seguir"
              danger
              type="primary"
              disabled={calculando || !impacto || nada}
              onClick={() => setPaso(2)}
            >
              Entiendo el impacto, continuar
            </Button>
          ),
        ]
      : paso === 2
        ? [
            <Button key="volver" onClick={() => setPaso(1)} disabled={eliminando}>
              Volver
            </Button>,
            <Button
              key="eliminar"
              danger
              type="primary"
              disabled={!confirmado}
              loading={eliminando}
              onClick={() => void eliminar()}
            >
              Eliminar definitivamente
            </Button>,
          ]
        : [
            <Button key="cerrar" type="primary" onClick={cerrar}>
              Cerrar
            </Button>,
          ];

  return (
    <Modal
      open={open}
      onCancel={cerrar}
      closable={!eliminando}
      keyboard={!eliminando}
      maskClosable={false}
      destroyOnClose
      width={680}
      className="alm-modal-eliminar"
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: "var(--error-color)" }} />
          <span>
            {paso === 1
              ? `Eliminar ${titulo} — revisa el impacto`
              : paso === 2
                ? `ÚLTIMA CONFIRMACIÓN — eliminar ${titulo}`
                : "Resultado de la eliminación"}
          </span>
        </Space>
      }
      footer={pie}
    >
      {paso === 1 ? paso1 : paso === 2 ? paso2 : paso3}
    </Modal>
  );
};

export default EliminarArchivosModal;
